import { CommonModule } from "@angular/common";
import { Component, inject, Inject } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { finalize, Observable, of, switchMap, take } from "rxjs";
import {
  AdminTasksService,
  WITHDRAWAL_UNAVAILABLE_MESSAGE,
  withdrawalRequestErrorMessage,
} from "../../../services/admin-tasks.service";
import { AuthService } from "../../../services/auth.service";
import { DocumentsService } from "../../../services/documents.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";

export interface WithdrawDialogData {
  accountBalance: number;
  isAdvanceWithdraw: boolean;
}

@Component({
  selector: "app-withdraw-dialog",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    DialogHeaderComponent,
    ButtonComponent,
  ],
  templateUrl: "./withdraw-dialog.component.html",
  styleUrl: "./withdraw-dialog.component.scss",
})
export class WithdrawDialogComponent {
  readonly dialogRef = inject(MatDialogRef<WithdrawDialogComponent>);
  private readonly _tasksService = inject(AdminTasksService);
  private readonly _documentsService = inject(DocumentsService);
  private readonly _authService = inject(AuthService);

  requestValue = new FormControl("R$ 0,00", {
    validators: [Validators.required, (control) => this.balanceValidator(control)],
  });

  readonly unavailableMessage = WITHDRAWAL_UNAVAILABLE_MESSAGE;
  readonly acceptedInvoiceTypes = ".pdf,.png,.jpg,.jpeg,.xml";
  accountBalance: number;
  isAdvanceWithdraw: boolean;
  /** PJ exige NF; PF não (mesmo critério do backend: user.company?). */
  readonly requiresInvoice: boolean;
  submitting = false;
  submitError = "";
  invoiceFile: File | null = null;
  invoiceError = "";

  get hasAvailableBalance(): boolean {
    return (Number(this.accountBalance) || 0) > 0;
  }

  get invoiceRequiredForSubmit(): boolean {
    return !this.isAdvanceWithdraw && this.requiresInvoice;
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: WithdrawDialogData) {
    this.accountBalance = data.accountBalance;
    this.isAdvanceWithdraw = data.isAdvanceWithdraw;
    this.requiresInvoice = !!this._authService.getLocalStorageUserInfo()?.company;
    this.requestValue.updateValueAndValidity();
  }

  onInvoiceSelected(event: Event): void {
    this.invoiceError = "";
    this.submitError = "";
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.invoiceFile = null;
      return;
    }

    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.invoiceFile = null;
      this.invoiceError = "A nota fiscal deve ter no máximo 10 MB.";
      input.value = "";
      return;
    }

    this.invoiceFile = file;
  }

  confirm() {
    this.submitError = "";
    this.invoiceError = "";
    this.requestValue.markAsTouched();
    this.requestValue.updateValueAndValidity();

    if (this.requestValue.hasError("invalidAmount")) {
      this.submitError = WITHDRAWAL_UNAVAILABLE_MESSAGE;
    }

    if (this.invoiceRequiredForSubmit && !this.invoiceFile) {
      this.invoiceError = "Anexe a nota fiscal para solicitar o saque.";
    }

    if (
      this.requestValue.invalid ||
      this.submitting ||
      (this.invoiceRequiredForSubmit && !this.invoiceFile)
    ) {
      return;
    }

    const value = this.requestValue?.value?.replace(/\D/g, "");
    const numericValue = parseFloat(value!) / 100 || 0;

    if (this.isAdvanceWithdraw) {
      alert("Implementar chamada na API");
      this.dialogRef.close();
      return;
    }

    this.submitting = true;
    this.uploadInvoiceIfNeeded()
      .pipe(
        switchMap((invoiceUrl) =>
          invoiceUrl
            ? this._tasksService.requestWithdrawal(numericValue, invoiceUrl, "Nota fiscal")
            : this._tasksService.requestWithdrawal(numericValue),
        ),
        take(1),
        finalize(() => (this.submitting = false)),
      )
      .subscribe({
        next: () => this.dialogRef.close(numericValue),
        error: (error) => {
          this.submitError = withdrawalRequestErrorMessage(error);
        },
      });
  }

  onInput(event: Event): void {
    this.submitError = "";
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, "");
    const numericValue = parseFloat(value) / 100 || 0;

    const formattedValue = numericValue.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    });

    this.requestValue.setValue(formattedValue);
  }

  balanceValidator(control: AbstractControl): ValidationErrors | null {
    const rawValue = control.value || "";
    const numericValue = parseFloat(rawValue.replace(/\D/g, "")) / 100 || 0;
    const available = Number(this.accountBalance) || 0;

    if (numericValue <= 0) {
      return { minAmount: true };
    }
    if (numericValue > available) {
      return { invalidAmount: true };
    }
    return null;
  }

  private uploadInvoiceIfNeeded(): Observable<string | undefined> {
    if (!this.invoiceFile) {
      return of(undefined);
    }
    return this._documentsService.uploadInvoice(this.invoiceFile);
  }
}
